// src/lib/statsModel.js — ONE transaction ledger, three views of it.
//
// Owner spec 2026-08-29 (§14): the Agents, Managers and General tabs must aggregate the same
// underlying truth. They do that by all calling `buildLedger` once and reading its output —
// no tab re-derives a transaction, and no tab counts a cooperation as two deals.
//
// ─── THE MODEL, AND WHY IT IS THIS SHAPE ──────────────────────────────────────
// One transaction has ONE gross commission. Out of that gross:
//     agent side     — what the participating agents take between them
//   + manager side   — the office manager's override, a % of the FULL gross (owner: it is
//                      "in addition", NOT carved out of the agent's share)
//   + company retained
//   = gross
// Verified against the owner's worked example before this file was written: $1,000 gross,
// one agent at 50% → $500 agent + $50 manager + $450 retained. A 50/50 cooperation → $250 +
// $250 + $50 + $450. Both reconcile exactly.
//
// ⚠️ THE SPLIT IS DERIVED, NOT STORED. There is no per-agent "agreed share" column anywhere.
// Legacy rows store each agent's OWN rate (viewing_agents.commission_pct) plus a
// lead_agent_id; core deals store neither, only participant roles. `agentDealIncome` in
// performance.js turns rates into money and IS the definition of the split — this module
// reads its output rather than re-implementing it, so there is one rule, not two.
// (`meeting_commission_split` exists in core with meeting_id/side/party_id/percent and is
// EMPTY — 0 rows on 2026-08-29. If it ever fills, it becomes the stored share and this
// derivation should defer to it.)
//
// 🚨 DO NOT "FIX" DEAL CREDIT TO 0.25 ON A 50/50 SPLIT. The written spec contradicts itself:
// §2 and its worked table say each agent carries 0.50, §14 says 0.25 for the same $250/$250
// deal — 0.25 being the share of GROSS rather than of the agent side. Owner ruling
// 2026-08-29: 0.50 is correct. The 0.25 reading came from a practice that has ENDED — a
// starting agent's first deal, where a senior went along to accompany them and the credit was
// scaled accordingly. That no longer happens: two people on a meeting is now simply a
// cooperation, and a cooperation already splits the commission, so nothing extra is owed to
// the arithmetic. Anyone reading §14 later and "correcting" this will break the property
// below, which is the one that makes credit reconcile.
//
// ⚠️ DEAL CREDIT IS A RATIO OF THE AGENT SIDE, NOT A COUNT. An agent on a 50/50 cooperation
// has 1 participation and 0.50 deal credit. Summing credit across agents gives back the
// number of transactions; summing participations does NOT (it double-counts cooperations),
// which is exactly the confusion the owner called out. Both numbers are reported, separately
// named, and never added together.
//
// ⚠️ A NEGATIVE RETAINED IS REPORTED, NOT HIDDEN. agentDealIncome's 3+-agent branch pays each
// agent their full own rate, so three agents at 50% would pay out 150% of gross and retained
// would go to −55%. No transaction in either database has more than 2 agents (measured
// 2026-08-29: legacy 38 solo / 17 pairs, core 11 solo / 5 pairs), so the branch has never
// executed. Rather than invent a splitting rule for a case that has never occurred, the ledger
// computes retained as a real subtraction and flags `overAllocated` when it goes negative.
// A wrong number that announces itself beats a plausible one that does not.

import { agentDealIncome, managerDealIncome, viewingCloseDate } from './performance.js'
import { toGel } from './rankingScore.js'
import { officeAt, officesCoveredAt, rateAt } from './spells.js'

// One transaction, fully allocated. `viewings` here are the merged rows from
// mergeDealSources (legacy + core), so this never sees two sources.
//
// Returns { transactions, byAgent, byManager, totals } where:
//   transactions — one entry per closed deal in range, with its allocation
//   byAgent      — agentId → { commission, dealCredit, participations, solo, coop }
//   byManager    — agentId → { commission, transactions }
//   totals       — the company-level reconciliation
// The manager override when the deal carries NO frozen one (owner, 2026-09-06: "every manager
// should receive 5% on commission"). Until now `manager_links == null` paid nothing — "an
// override never recorded was never owed" — which was defensible while every close stamped it.
// It is not true any more: a close recorded by event only (a meeting without a client, or a
// close straight from the ad) never reaches the stamper, and a pre-0796 deal never met it. On
// 2026-09-06 that read ₾174 of override on ₾6,040 of team gross where the rule says ₾302.
// Derived from the DATED tables — who covered the closing agent's office ON THE CLOSE DATE —
// at that manager's roster rate, never on their own deal, split across offices when a co-broke
// spans two. `[]` (stamped, nobody owed) stays `[]`: that is a recorded fact, not an absence.
function derivedManagerLinks(links, date, roster, history) {
  const closers = new Set(links.map(l => l.agent_id))
  const offices = new Set()
  for (const l of links) {
    const office = (history ? officeAt(l.agent_id, date, history.memberships) : null)
      ?? roster.find(a => a.id === l.agent_id)?.office_id ?? null
    if (office) offices.add(office)
  }
  if (!offices.size) return []
  const out = []
  for (const m of roster) {
    // AS-OF, not current (scheduler 0025). The dated spell is the answer; the roster column is
    // the fallback for a period no spell covers — which is every period before the rates were
    // dated, so this stays bit-identical for historical deals while making every change from
    // now on apply only after its effective date.
    const rate = history?.rates
      ? (rateAt(m.id, 'manager', date, history.rates) ?? Number(m.commission_manager))
      : Number(m.commission_manager)
    if (!(rate > 0) || closers.has(m.id)) continue
    const covered = history
      ? officesCoveredAt(m.id, date, history.coverage)
      : (m.office_id && m.is_active !== false ? [m.office_id] : [])
    const mine = [...offices].filter(o => covered.includes(o))
    if (!mine.length) continue
    out.push({ agent_id: m.id, commission_pct: rate,
      ...(offices.size > 1 ? { office_id: mine[0], office_share_percent: 100 / offices.size } : {}) })
  }
  return out
}

export function buildLedger(viewings, from, to, fx, roster = [], history = null) {
  const transactions = []
  const byAgent = new Map()
  const byManager = new Map()

  for (const v of viewings) {
    if (v.status !== 'closed') continue
    const date = viewingCloseDate(v)
    if (from && date < from) continue
    if (to && date > to) continue

    const gross = toGel(v.commission_amount, v.commission_currency, fx)
    const links = v.agent_links || (v.agent_ids || []).map(id => ({ agent_id: id, commission_pct: null }))
    // Fill a missing rate from the roster — the same reason coreDealsToViewings does it:
    // leaving another agent at 0 makes the two-agent branch price the whole deal wrong.
    // The deal's OWN frozen rate wins (that is what a snapshot is for). Only when the deal
    // carries none do we reconstruct it — and then from the spell that covered the close date,
    // falling back to the roster's current number when no spell does.
    const filled = links.map(l => ({
      agent_id: l.agent_id,
      commission_pct: l.commission_pct != null ? l.commission_pct
        : ((history?.rates ? rateAt(l.agent_id, 'agent', date, history.rates) : null)
           ?? (Number(roster.find(a => a.id === l.agent_id)?.commission) || 0)),
    }))

    const shares = filled.map(l => ({
      agent_id: l.agent_id,
      commission: agentDealIncome(gross, filled, l.agent_id, v.lead_agent_id),
    }))
    const agentSide = shares.reduce((s, x) => s + x.commission, 0)
    // ⚠️ NORMALISE THE KEY FIRST. The same field arrives under two names: the core column
    // stores `scheduler_agent_id`, the /agent-deals endpoint renames it to `agent_id`, and the
    // legacy scheduler table uses `agent_id`. Reading only one of them does not fail loudly —
    // `l.agent_id === undefined` matches the FIRST link, so the override is silently credited
    // to a manager called `undefined`. Caught on 2026-08-29 by a report that queried the
    // database directly instead of going through the endpoint.
    const mLinks = v.manager_links == null
      ? derivedManagerLinks(filled, date, roster, history)
      : v.manager_links.map(m => ({
          agent_id: m.agent_id ?? m.scheduler_agent_id,
          commission_pct: m.commission_pct,
          ...(m.office_id ? { office_id: m.office_id } : {}),
          ...(m.office_share_percent != null ? { office_share_percent: m.office_share_percent } : {}),
        })).filter(m => m.agent_id)
    const managers = mLinks.map(m => ({
      agent_id: m.agent_id,
      commission: managerDealIncome(gross, mLinks, m.agent_id),
    })).filter(m => m.commission > 0)
    const managerSide = managers.reduce((s, x) => s + x.commission, 0)
    const retained = gross - agentSide - managerSide

    const isCoop = filled.length > 1
    for (const s of shares) {
      // Credit is the agent's share OF THE AGENT SIDE. When the agent side is 0 (a deal with
      // no commission recorded), credit is 0 rather than NaN — an unpriced deal still counts
      // as a participation, which is the honest split of "was there" from "earned from it".
      const credit = agentSide > 0 ? s.commission / agentSide : 0
      const a = byAgent.get(s.agent_id) || { commission: 0, dealCredit: 0, participations: 0, solo: 0, coop: 0 }
      a.commission += s.commission
      a.dealCredit += credit
      a.participations += 1
      if (isCoop) a.coop += 1; else a.solo += 1
      byAgent.set(s.agent_id, a)
    }
    for (const m of managers) {
      const e = byManager.get(m.agent_id) || { commission: 0, transactions: 0 }
      e.commission += m.commission
      e.transactions += 1
      byManager.set(m.agent_id, e)
    }

    transactions.push({
      id: v.id, date, gross, agentSide, managerSide, retained,
      overAllocated: retained < 0,
      agents: shares, managers, isCoop,
      source: v.source === 'core' ? 'core' : 'legacy',
    })
  }

  const totals = transactions.reduce((t, x) => ({
    transactions: t.transactions + 1,
    gross: t.gross + x.gross,
    agentSide: t.agentSide + x.agentSide,
    managerSide: t.managerSide + x.managerSide,
    retained: t.retained + x.retained,
    overAllocated: t.overAllocated + (x.overAllocated ? 1 : 0),
  }), { transactions: 0, gross: 0, agentSide: 0, managerSide: 0, retained: 0, overAllocated: 0 })

  return { transactions, byAgent, byManager, totals }
}

// The reconciliation the owner asked for, as a checkable statement rather than a comment.
// Rounding is per-transaction (each agent's cut is rounded to the tetri), so the parts can
// differ from the gross by a few units across many deals — `slack` reports that honestly
// instead of a boolean that would be false for an uninteresting reason.
export function reconcile(totals) {
  const parts = totals.agentSide + totals.managerSide + totals.retained
  return { gross: totals.gross, parts, slack: parts - totals.gross, ok: Math.abs(parts - totals.gross) < 1 }
}

// Team = the office. There is no manager_id or team table in this schema; office_id is the
// only real grouping and is already what managers are scoped to in App.jsx. Documented in
// performance.js managerLinksForDeal — kept consistent here on purpose.
export function teamOf(manager, roster) {
  if (!manager?.office_id) return []
  return roster.filter(a => a.office_id === manager.office_id && a.id !== manager.id)
}

// Managers are the agents carrying a team rate. Keyed on the RATE being set, not on the role
// string, for the same reason performance.js is: roles are admin-editable free text.
export function managersIn(roster) {
  return roster.filter(a => Number(a.commission_manager) > 0)
}

// ─── DATED ATTRIBUTION (owner ruling 2026-08-29) ──────────────────────────────
// "Managers stats must come from their active manager date. Every action recorded must
// include who the manager was at that time … so next stats won't mangle if any variable
// changes."
//
// 🚨 WHY teamOf() ABOVE IS NOT ENOUGH, measured before this was written: it reads
// agents.office_id, i.e. where everyone sits TODAY. On 2026-08-29 that showed Luka Bigvava
// 26 team deals / ₾15,016 over 90 days when the truthful figures were 7 / ₾6,673 — he had
// only been manager of that office since 2026-08-19. 19 deals and ₾8,343 were attributed to
// a period he did not manage. Every later transfer would silently rewrite it again.
//
// The scheduler already had the right tables (migration 0001, 2026-08-06):
//   agent_office_membership — dated, trigger-maintained on every agents.office_id write
//   office_manager          — dated, and lets a manager cover an office they do not sit in
// They were simply never read. These helpers read them.
//
// Intervals are [joined, left) on DAY precision, matching how close dates are stored. A NULL
// left means "still open"; a row that opens and closes the same day covers that day.

// The three as-of readers moved to ./spells.js (2026-09-20) so `performance.js` can use them
// without closing an ESM import cycle with this module. Re-exported here because StatsPanels
// and the tests import them from statsModel by name, and a move is not a reason to break them.
export { officeAt, officesCoveredAt, rateAt } from './spells.js'

// ✅ A CROSS-OFFICE CO-BROKE COUNTS FOR BOTH MANAGERS, EACH AT THEIR FULL RATE — owner ruling
// 2026-08-29, confirmed explicitly when the consequence was put to them: two offices on one
// deal means two managers take their percentage of the SAME gross, so a deal with two 5%
// managers pays out 10% of it in overrides. That is intended, not an oversight. Do not
// "fix" it into a split; the office that contributed an agent earns its manager's share.
//
// The transactions that belong to this manager's team, judged AT EACH DEAL'S CLOSE DATE.
// A deal counts when someone other than the manager closed it while sitting in an office the
// manager covered that day. Self-closes are excluded for the same reason the override is:
// one person, one share, per deal.
export function teamTransactionsAt(managerId, transactions = [], memberships = [], coverage = []) {
  return transactions.filter(tx => {
    if (!tx.date) return false
    const offices = officesCoveredAt(managerId, tx.date, coverage)
    if (!offices.length) return false
    return tx.agents.some(a => a.agent_id !== managerId
                            && offices.includes(officeAt(a.agent_id, tx.date, memberships)))
  })
}

// Everyone who was a manager at any point in [from, to] — from the DATED table, not from
// whether a commission rate happens to be set. Two managers (Tako Kharkhelauri, Gvantsa
// Karpovi) were invisible on 2026-08-29 because the tab keyed on commission_manager > 0 and
// they had no rate; managing an office is a fact about the roster, not about being paid.
export function managersInPeriod(coverage = [], roster = [], from, to) {
  const ids = new Set(coverage
    .filter(c => (!from || !c.left_at || c.left_at > from) && (!to || c.joined_at <= to))
    .map(c => c.agent_id))
  return roster.filter(a => ids.has(a.id))
}

// The team as it stands right now, for headcount only. Deliberately separate from the dated
// helpers above: "how many people do you manage today" and "whose work was yours back then"
// are different questions and must not share one answer.
export function currentTeamAt(managerId, roster = [], memberships = [], coverage = [], onDate) {
  const offices = officesCoveredAt(managerId, onDate, coverage)
  if (!offices.length) return []
  return roster.filter(a => a.id !== managerId && a.is_active !== false
                         && offices.includes(officeAt(a.id, onDate, memberships)))
}

// ─── WHO APPEARS IN THE LISTS (owner ruling 2026-08-29) ───────────────────────
//   admin / general_manager : active AND past managers, active AND past agents
//   manager                 : ACTIVE managers only, active AND past agents
//   anyone else (agent)     : active agents only
//
// Past people matter because a period in the past contained them: a 90-day window that hides
// the 70 resigned agents would report the company's own history as if only today's 14 people
// had ever worked. A manager sees past AGENTS for that reason, but only current MANAGERS —
// their peers are who they work alongside now.
//
// Returned as a predicate pair rather than two filtered arrays, so a caller cannot accidentally
// apply the agent rule to the manager list.
export function visibilityFor(role) {
  const full = role === 'admin' || role === 'general_manager'
  return {
    // is_active !== false, because a row with the flag missing is a current agent, not a past
    // one — treating unknown as "gone" would silently drop people from every list.
    agents:   full || role === 'manager' ? () => true : a => a.is_active !== false,
    managers: full ? () => true : a => a.is_active !== false,
    showsPast: full || role === 'manager',
  }
}

// ─── PERIOD COMPARISON ────────────────────────────────────────────────────────
// Change from the previous window of the same length, as the Desk board's movement arrows
// do. null when there is nothing to compare against (no previous value at all); `fresh` when
// the previous period was zero and this one is not — "+∞%" is not a number anyone can read.
export function pctDelta(cur, prev) {
  const c = Number(cur) || 0, p = Number(prev)
  if (prev == null || !Number.isFinite(p)) return null
  if (p === 0) return c === 0 ? { pct: 0, dir: 0 } : { pct: null, dir: 1, fresh: true }
  const pct = Math.round((c - p) / Math.abs(p) * 100)
  return { pct, dir: pct > 0 ? 1 : pct < 0 ? -1 : 0 }
}

// The window of the same length that ends the day before `from`.
export function previousWindow(from, to) {
  const d = s => new Date(s + 'T12:00:00')
  const days = Math.round((d(to) - d(from)) / 86400000) + 1
  const pTo = d(from); pTo.setDate(pTo.getDate() - 1)
  const pFrom = new Date(pTo); pFrom.setDate(pFrom.getDate() - days + 1)
  const str = x => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
  return { from: str(pFrom), to: str(pTo), days }
}

// ─── MANAGER HIRE COHORTS (the 2026-08-31 audit's "do not calculate yet" list) ─────────
// Definitions live HERE and nowhere else (audit step 2):
//   • a HIRE belongs to the manager who covered the office the agent joined, ON THE HIRE DATE
//     (office_manager × agent_office_membership, both dated; the agent's current office_id
//     is the fallback only when no membership row covers that day);
//   • STARTED = the agent's first attendance check-in; hire→start is that gap in days;
//   • FIRST DEAL = the earliest ledger transaction the agent took part in, on or after hire;
//   • NEVER EARNED counts only hires that have MATURED — 90 days on the roster — so a
//     three-week-old hire is not called a failure; the policy is the number below;
//   • RETAINED at N days = still on the roster N days after hire: no resign_date on or before
//     hire+N, and not inactive-without-a-date. Only hires whose hire+N is in the past count.
// Medians, not means: one agent who took a year to start would otherwise define the team.
export const COHORT_MATURITY_DAYS = 90
export const RETENTION_HORIZONS = [30, 90, 180]

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00'); d.setDate(d.getDate() + n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function daysBetween(a, b) {
  return Math.round((new Date(b + 'T12:00:00') - new Date(a + 'T12:00:00')) / 86400000)
}
function median(xs) {
  const v = xs.filter(x => Number.isFinite(x)).sort((a, b) => a - b)
  if (!v.length) return null
  const mid = v.length >> 1
  return v.length % 2 ? v[mid] : Math.round((v[mid - 1] + v[mid]) / 2)
}

// Hires attributed to `managerId` with hire_date in [from, to].
export function hiresOf(managerId, roster = [], memberships = [], coverage = [], from, to) {
  return roster.filter(a => {
    if (!a.hire_date || a.id === managerId) return false
    if (from && a.hire_date < from) return false
    if (to && a.hire_date > to) return false
    const covered = officesCoveredAt(managerId, a.hire_date, coverage)
    if (!covered.length) return false
    const office = officeAt(a.id, a.hire_date, memberships) ?? a.office_id ?? null
    return office != null && covered.includes(office)
  })
}

// firstCheckins: Map(agent_id → 'YYYY-MM-DD'); transactions: an ALL-TIME ledger's transactions.
export function cohortMetrics({ managerId, roster = [], memberships = [], coverage = [], firstCheckins = new Map(),
                                transactions = [], from, to, today }) {
  const hires = hiresOf(managerId, roster, memberships, coverage, from, to)
  // Every deal date per agent, so the first deal ON OR AFTER the hire can be found. A single
  // global minimum would take a pre-hire deal (a re-hire, an import) and then declare the agent
  // dealless — the exact case the test pins.
  const dealDates = new Map()
  for (const tx of transactions) {
    if (!tx.date) continue
    for (const a of tx.agents) (dealDates.get(a.agent_id) || dealDates.set(a.agent_id, []).get(a.agent_id)).push(tx.date)
  }
  const firstDealAfter = (agentId, hire) => {
    const ds = (dealDates.get(agentId) || []).filter(d => d >= hire).sort()
    return ds[0] || null
  }
  const hireToStart = [], rampUp = []
  let matured = 0, neverEarned = 0, started = 0, earned = 0
  const retention = Object.fromEntries(RETENTION_HORIZONS.map(n => [n, { kept: 0, of: 0 }]))
  for (const a of hires) {
    const start = firstCheckins.get(a.id)
    if (start) { started++; hireToStart.push(Math.max(0, daysBetween(a.hire_date, start))) }
    const dealAfterHire = firstDealAfter(a.id, a.hire_date)
    if (dealAfterHire) { earned++; rampUp.push(daysBetween(a.hire_date, dealAfterHire)) }
    if (daysBetween(a.hire_date, today) >= COHORT_MATURITY_DAYS) { matured++; if (!dealAfterHire) neverEarned++ }
    for (const n of RETENTION_HORIZONS) {
      const horizon = addDays(a.hire_date, n)
      if (horizon > today) continue
      retention[n].of++
      const left = (a.resign_date && a.resign_date <= horizon) || (a.is_active === false && !a.resign_date)
      if (!left) retention[n].kept++
    }
  }
  return {
    hires: hires.length, started, earned,
    hireToStartMedian: median(hireToStart), rampUpMedian: median(rampUp),
    neverEarned: { n: neverEarned, of: matured },
    retention,
  }
}
